import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, Users, Eye, MapPin, Globe, Clock, TrendingUp, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ITEMS_PER_PAGE = 25;

const StatisticsManager = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setCurrentPage(1); // Reset to first page on refresh
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Eroare la încărcarea statisticilor');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-600" />
        <span className="ml-3 text-slate-600">Se încarcă statisticile...</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center p-12 text-slate-500">
        <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Nu există date de statistici încă.</p>
        <p className="text-sm mt-2">Vizitele vor fi înregistrate automat când utilizatorii accesează site-ul.</p>
      </div>
    );
  }

  // Prepare chart data
  const maxVisits = Math.max(...(stats.visits_per_day?.map(d => d.visits) || [1]), 1);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with refresh */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Statistici Site
        </h2>
        <Button onClick={fetchStatistics} variant="outline" disabled={loading} size="sm">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Actualizează</span>
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-xs sm:text-sm">Total Vizualizări</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.summary?.total_visits || 0}</p>
              </div>
              <Eye className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-xs sm:text-sm">Vizualizări Azi</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.summary?.today_visits || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs sm:text-sm">Vizitatori Unici Azi</p>
                <p className="text-2xl sm:text-3xl font-bold">{stats.summary?.today_unique_visitors || 0}</p>
              </div>
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visits Chart (last 30 days) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-600" />
            Vizualizări Ultimele 30 Zile
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.visits_per_day?.length > 0 ? (
            <div className="space-y-2">
              {/* Simple bar chart */}
              <div className="flex items-end gap-1 h-40 border-b border-slate-200 pb-2">
                {stats.visits_per_day.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center group relative">
                    <div 
                      className="w-full bg-cyan-500 hover:bg-cyan-600 transition-colors rounded-t min-h-[4px]"
                      style={{ height: `${(day.visits / maxVisits) * 100}%` }}
                    />
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                      {formatShortDate(day._id)}: {day.visits} vizite, {day.unique_visitors} unici
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-slate-500">
                <span>{formatShortDate(stats.visits_per_day[0]?._id)}</span>
                <span>{formatShortDate(stats.visits_per_day[stats.visits_per_day.length - 1]?._id)}</span>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Nu există date pentru această perioadă</p>
          )}
        </CardContent>
      </Card>

      {/* Two columns: Regions and Countries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Visits by Region */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
              Vizite pe Regiuni/Orașe
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[300px] overflow-y-auto">
            {stats.visits_by_region?.length > 0 ? (
              <div className="space-y-2">
                {stats.visits_by_region.map((region, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-base font-bold text-slate-400 w-5 flex-shrink-0">{idx + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">{region._id?.city || 'Necunoscut'}</p>
                        <p className="text-xs text-slate-500 truncate">{region._id?.region}, {region._id?.country}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="font-bold text-cyan-600">{region.visits}</p>
                      <p className="text-xs text-slate-500">{region.unique_visitors} unici</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nu există date geografice</p>
            )}
          </CardContent>
        </Card>

        {/* Visits by Country */}
        <Card>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
              Vizite pe Țări
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.visits_by_country?.length > 0 ? (
              <div className="space-y-2">
                {stats.visits_by_country.map((country, idx) => {
                  const maxCountryVisits = stats.visits_by_country[0]?.visits || 1;
                  const percentage = (country.visits / maxCountryVisits) * 100;
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium truncate">{country._id || 'Necunoscut'}</span>
                        <span className="text-cyan-600 font-bold ml-2">{country.visits}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nu există date pe țări</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Pages and Hourly Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Pagini Populare
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_pages?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_pages.map((page, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-lg font-bold text-slate-400 w-6">{idx + 1}</span>
                      <span className="text-sm truncate">{page._id || '/'}</span>
                    </div>
                    <span className="font-bold text-cyan-600 ml-2">{page.visits}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nu există date</p>
            )}
          </CardContent>
        </Card>

        {/* Visits by Hour */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              Vizite pe Ore (Azi)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.visits_by_hour?.length > 0 ? (
              <div className="flex items-end gap-1 h-32">
                {Array.from({ length: 24 }, (_, hour) => {
                  const hourStr = hour.toString().padStart(2, '0');
                  const hourData = stats.visits_by_hour.find(h => h._id === hourStr);
                  const visits = hourData?.visits || 0;
                  const maxHourVisits = Math.max(...stats.visits_by_hour.map(h => h.visits), 1);
                  const height = visits > 0 ? Math.max((visits / maxHourVisits) * 100, 10) : 4;
                  
                  return (
                    <div key={hour} className="flex-1 flex flex-col items-center group relative">
                      <div 
                        className={`w-full rounded-t transition-colors ${visits > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-slate-200'}`}
                        style={{ height: `${height}%` }}
                      />
                      {visits > 0 && (
                        <div className="absolute bottom-full mb-1 hidden group-hover:block bg-slate-800 text-white text-xs px-1 py-0.5 rounded">
                          {hourStr}:00 - {visits}
                        </div>
                      )}
                      {hour % 4 === 0 && (
                        <span className="text-[10px] text-slate-400 mt-1">{hourStr}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nu există date pentru azi</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Visits Table with Pagination */}
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" />
            Ultimii 100 Vizitatori
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 sm:p-6">
          {stats.recent_visits?.length > 0 ? (
            <div className="space-y-4">
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table className="min-w-[500px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 text-xs sm:text-sm">#</TableHead>
                      <TableHead className="text-xs sm:text-sm">Data/Ora</TableHead>
                      <TableHead className="text-xs sm:text-sm">Pagină</TableHead>
                      <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Locație</TableHead>
                      <TableHead className="text-xs sm:text-sm hidden md:table-cell">IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.recent_visits
                      .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                      .map((visit, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-slate-400 font-mono text-xs sm:text-sm py-2">
                            {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs sm:text-sm py-2">
                            {formatDate(visit.visited_at)}
                          </TableCell>
                          <TableCell className="max-w-[120px] sm:max-w-[200px] truncate text-xs sm:text-sm py-2">
                            {visit.page_url || '/'}
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm py-2 hidden sm:table-cell">
                            {visit.city && visit.region ? (
                              <span className="truncate max-w-[150px] inline-block">{visit.city}, {visit.region}</span>
                            ) : visit.country ? (
                              <span>{visit.country}</span>
                            ) : (
                              <span className="text-slate-400">Local</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 font-mono py-2 hidden md:table-cell">
                            {visit.ip_address?.substring(0, 12)}...
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {stats.recent_visits.length > ITEMS_PER_PAGE && (
                <div className="flex flex-col sm:flex-row items-center justify-between border-t pt-4 gap-3">
                  <p className="text-xs sm:text-sm text-slate-600 order-2 sm:order-1">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, stats.recent_visits.length)} din {stats.recent_visits.length}
                  </p>
                  <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8 px-2 sm:px-3"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Anterior</span>
                    </Button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.ceil(stats.recent_visits.length / ITEMS_PER_PAGE) }, (_, i) => (
                        <Button
                          key={i + 1}
                          variant={currentPage === i + 1 ? "default" : "outline"}
                          size="sm"
                          className={`w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm ${currentPage === i + 1 ? 'bg-cyan-600' : ''}`}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(Math.ceil(stats.recent_visits.length / ITEMS_PER_PAGE), p + 1))}
                      disabled={currentPage >= Math.ceil(stats.recent_visits.length / ITEMS_PER_PAGE)}
                      className="h-8 px-2 sm:px-3"
                    >
                      <span className="hidden sm:inline mr-1">Următor</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Nu există vizite recente</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatisticsManager;
